/**
 * D2C Freight Pricing Agent — shared constants
 * ------------------------------------------------------------------
 * The module is segregated into three categories (workspaces), each
 * gated by its own real JWT accessList flag:
 *
 *   PROPOSAL_CREATE → Sales Leader / BD workspace
 *   PROPOSAL_L1     → L1 Pricing Reviewer (Approver) workspace
 *   PROPOSAL_ADMIN  → Functional Head + Admin workspace
 *
 * Give a user one of these flags and they see only that workspace.
 * Routing + sidebar visibility both key off these flags (see
 * menuConfig.js, PrivateRoute.js and ReduxHelpers PATH_ACCESS_MAP).
 */

// ── Access flags (the three real backend modules) ────────────────────
export const ACCESS = {
  SL: 'PROPOSAL_CREATE',
  L1: 'PROPOSAL_L1',
  FH: 'PROPOSAL_ADMIN',
}

export const ALL_PRICING_ACCESS = [ACCESS.SL, ACCESS.L1, ACCESS.FH]

// ⚠️ TEMP testing bypass — the backend does not yet issue the PROPOSAL_* modules
// in the JWT, so no one would see the module with gating on. While false, the
// sidebar item + routes are open to any logged-in user. FLIP TO `true` to enforce
// real per-role gating (menuConfig roles + PrivateRoute guards both read this).
export const D2C_ROLE_GATING = true

// ── Route paths ──────────────────────────────────────────────────────
export const PATHS = {
  // Sales Leader
  slDashboard: '/d2c-pricing/sl/dashboard',
  slQueue: '/d2c-pricing/sl/approval-queue',
  slHistory: '/d2c-pricing/sl/history',
  slCounterReview: '/d2c-pricing/sl/counter-review',
  // L1 Pricing
  l1Dashboard: '/d2c-pricing/l1/dashboard',
  l1Queue: '/d2c-pricing/l1/approval-queue',
  l1Review: '/d2c-pricing/l1/review',
  l1History: '/d2c-pricing/l1/history',
  // Functional Head
  fhDashboard: '/d2c-pricing/fh/dashboard',
  fhQueue: '/d2c-pricing/fh/approval-queue',
  fhReview: '/d2c-pricing/fh/review',
  fhRules: '/d2c-pricing/fh/rules',
  fhMasters: '/d2c-pricing/fh/masters',
  fhHistory: '/d2c-pricing/fh/history',
  // Shared (available to all three categories)
  newProposal: '/d2c-pricing/new-proposal',
  gtmToolkit: '/d2c-pricing/resources/gtm-toolkit',
  guide: '/d2c-pricing/resources/guide',
}

// Tab routing: pick the landing workspace from the JWT accessList
// (FH wins over L1 wins over SL, per the integration guide).
export function defaultWorkspacePath(accessList = []) {
  if (accessList.includes(ACCESS.FH)) return PATHS.fhDashboard
  if (accessList.includes(ACCESS.L1)) return PATHS.l1Dashboard
  return PATHS.slDashboard
}

// ── Margin buckets (what the Sales Leader sees — never the number) ────
// Colour-coded; thresholds are FH-editable in the Rules page.
export const BUCKETS = {
  HEALTHY: { key: 'HEALTHY', label: 'Healthy Margins', range: 'above 20%', color: '#1D4ED8', bg: '#DBEAFE' },
  STRONG: { key: 'STRONG', label: 'Strong Margins', range: '15% – 20%', color: '#15803D', bg: '#ECFDF5' },
  MODERATE: { key: 'MODERATE', label: 'Moderate Margins', range: '10% – 15%', color: '#4D7C0F', bg: '#ECFCCB' },
  BORDERLINE: { key: 'BORDERLINE', label: 'Borderline Profitable', range: '0% – 10%', color: '#A16207', bg: '#FEF9C3' },
  LOSS: { key: 'LOSS', label: 'Loss Making', range: 'below 0%', color: '#B91C1C', bg: '#FEE2E2' },
}

export const BUCKET_LIST = [BUCKETS.HEALTHY, BUCKETS.STRONG, BUCKETS.MODERATE, BUCKETS.BORDERLINE, BUCKETS.LOSS]

// ── Approval routes (Flow 1/2/3) ─────────────────────────────────────
export const ROUTES_FLOW = [
  {
    key: 'route1',
    label: 'ROUTE 1 · L1 approval mandatory',
    range: 'above 20%',
    color: 'info',
    desc: 'Even Healthy deals must be approved by the L1 Pricing Reviewer before sharing. The Functional Head sees everything read-only; every move is logged.',
  },
  {
    key: 'route2',
    label: 'ROUTE 2 · L1 approval mandatory',
    range: '10% – 20%',
    color: 'warning',
    desc: "Covers Strong and Moderate buckets. L1 approval is enough when nothing is changed. Any change — or an escalation — sends the deal up to the Functional Head.",
  },
  {
    key: 'route3',
    label: 'ROUTE 3 · Functional Head approval mandatory',
    range: 'below 10%',
    color: 'error',
    desc: 'Covers Borderline Profitable and Loss Making. Deal is pushed to Pricing, the order-level data template auto-downloads, Pricing rebuilds the working and the Functional Head signs off. Every iteration ends at the Functional Head.',
  },
]

// ── Deal lifecycle statuses (visible on dashboards) ──────────────────
export const LIFECYCLE = [
  { key: 'DRAFT', label: 'Draft', note: 'nothing routed yet' },
  { key: 'GENERATED', label: 'Generated', note: 'margin worked out, buckets set' },
  { key: 'PENDING_L1', label: 'Pending at L1', note: 'Route 1, 2, or pushed from Route 3' },
  { key: 'AWAITING_ORDER_DATA', label: 'Awaiting Order-Level Data', note: 'Route 3 — template auto-downloaded' },
  { key: 'PENDING_FH', label: 'Pending with Functional Head', note: 'rebuilds · pricing-authored · escalations' },
  { key: 'APPROVED', label: 'Approved', note: 'sharing unlocked' },
  { key: 'SENT', label: 'Sent', note: 'logged' },
  { key: 'CLOSED', label: 'Converted / Lost', note: 'written back to the tracker' },
]

// ── Status chip colour mapping (MUI palette keys / custom hex) ───────
// Returns { color, bg } for a status label string used across screens.
export const STATUS_STYLES = {
  draft: { color: '#4B5563', bg: '#F3F4F6' },
  pendingL1: { color: '#A16207', bg: '#FEF9C3' },
  pendingFh: { color: '#9A3412', bg: '#FFF7ED' },
  approved: { color: '#15803D', bg: '#ECFDF5' },
  shared: { color: '#15803D', bg: '#ECFDF5' },
  converted: { color: '#15803D', bg: '#ECFDF5' },
  returned: { color: '#B91C1C', bg: '#FEE2E2' },
  lost: { color: '#B91C1C', bg: '#FEE2E2' },
  awaiting: { color: '#A16207', bg: '#FEF9C3' },
  neutral: { color: '#4B5563', bg: '#F3F4F6' },
  purple: { color: '#5B2D8E', bg: '#F4EEFA' },
  orange: { color: '#E65100', bg: '#FDF1E5' },
}

// ── Commercial card types (Step 1 of New Proposal) ───────────────────
export const COMMERCIAL_TYPES = [
  { key: 'blended', title: 'Blended', desc: 'One card; Prozo picks the courier per shipment.' },
  { key: 'cpwise', title: 'Courier-wise', desc: 'A separate rate card for each courier you choose.' },
  // SDD/NDD and H&B are no longer commercial types — they're service scopes chosen in the
  // service funnel (SDD/NDD = a Forward level; H&B = the Large parcel size, which derives the
  // HB mini-agreement at create time). Only true packaging choices live here now.
  { key: 'standard', title: 'Standard', desc: 'Pre-built card — no pricing engine, no approval.' },
]

// ── Services ─────────────────────────────────────────────────────────
export const SERVICES = [
  { key: 'srf', label: 'FWD Surface' },
  { key: 'air', label: 'FWD Air' },
  { key: 'sddndd', label: 'SDD & NDD' },
  { key: 'rvp', label: 'Reverse (RVP)' },
]

// ── New Proposal wizard steps ────────────────────────────────────────
export const WIZARD_STEPS = [
  { key: 's1', label: 'Prospect Details' },
  { key: 's2', label: 'RFI Details' },
  { key: 's3', label: 'Commercial Expectations' },
  { key: 's4', label: 'Review & Next Steps' },
]

// ── Pincode zone classes ─────────────────────────────────────────────
export const ZONE_CLASSES = {
  Metro: { label: 'Metro', color: '#1D4ED8', bg: '#DBEAFE' },
  Special: { label: 'Special', color: '#E65100', bg: '#FDF1E5' },
  Standard: { label: 'Standard', color: '#4B5563', bg: '#F3F4F6' },
}

export const RFI_ZONES = ['Local', 'Within State', 'Metro', 'ROI', 'Special']
